import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { Button } from '../common/index.js';

const DateFilterDropdown = ({ filter, setFilter, customDates, setCustomDates, onApply, dateRange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(filter);
  const dropdownRef = useRef(null);

  const filterOptions = [
    { value: 'current_month', label: 'Current Month' },
    { value: 'last_3_months', label: 'Last 3 Months' },
    { value: 'last_6_months', label: 'Last 6 Months' },
    { value: 'last_year', label: 'Last Year' },
    { value: 'all', label: 'All Time' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const getFilterLabel = () => {
    const option = filterOptions.find(opt => opt.value === filter);
    return option ? option.label : 'Select Period';
  };

  useEffect(() => {
    setSelectedOption(filter);
  }, [filter]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFilterChange = (value) => {
    if (value === 'custom') {
      setSelectedOption(value);
    } else {
      setSelectedOption(value);
      setFilter(value);
      setIsOpen(false);
      if (onApply) onApply();
    }
  };

  const handleCustomApply = () => {
    if (customDates.from_date && customDates.to_date) {
      setFilter('custom');
      setIsOpen(false);
      if (onApply) onApply();
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        size="sm"
        icon={Calendar}
        onClick={() => setIsOpen(!isOpen)}
        className="gap-2"
      >
        {getFilterLabel()}
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </Button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-card shadow-card z-50">
          <div className="p-4">
            <h3 className="text-sm font-semibold text-secondary mb-3">Select Date Range</h3>

            <div className="space-y-1 mb-4">
              {filterOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange(option.value)}
                  className={`w-full text-left px-3 py-2 rounded-btn text-sm transition-colors ${
                    selectedOption === option.value
                      ? 'bg-brand-light text-brand font-medium'
                      : 'hover:bg-gray-50 text-secondary'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {selectedOption === 'custom' && (
              <div className="border-t border-gray-200 pt-4">
                <label className="block text-xs font-medium text-secondary mb-1">From Date</label>
                <input
                  type="date"
                  value={customDates.from_date}
                  onChange={(e) => setCustomDates({ ...customDates, from_date: e.target.value })}
                  className="input-clean mb-3"
                />

                <label className="block text-xs font-medium text-secondary mb-1">To Date</label>
                <input
                  type="date"
                  value={customDates.to_date}
                  onChange={(e) => setCustomDates({ ...customDates, to_date: e.target.value })}
                  className="input-clean mb-3"
                />

                <Button
                  onClick={handleCustomApply}
                  disabled={!customDates.from_date || !customDates.to_date}
                  className="w-full"
                  size="sm"
                >
                  Apply Custom Range
                </Button>
              </div>
            )}

            {dateRange && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-secondary">
                  <span className="font-medium">Selected Period:</span>
                  <br />
                  {new Date(dateRange.start_date).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })} - {new Date(dateRange.end_date).toLocaleDateString('en-IN', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  })}
                  <br />
                  <span className="text-gray-500">({dateRange.days} days)</span>
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DateFilterDropdown;
